// NOTE: Split into multiple files when groups of related functions are identified.

import graphql from '../graphql';
import apolloClient from '../apollo';
import * as QUESTION_TYPES from '~/constants/question_types';
import { NO_CACHE } from '~/constants/globals';

const fetchKioskSurveys = async (companyId, id) => {
  const { data } = await apolloClient.query({
    query: graphql.getKioskSurveys,
    variables: {
      companyId: Number(companyId),
      id,
    },
  });

  return data.kioskSurveys;
};

const fetchKioskCompany = async (languageISOCode, code, type) => {
  const { data } = await apolloClient.query({
    query: graphql.getKioskCompany,
    variables: {
      languageISOCode,
      field: String(type),
      value: String(code),
    },
    fetchPolicy: 'network-only',
  });

  return data.kioskCompany;
};

const fetchEmployeeSurvey = async (surveyCode, previewEmployeeId = null) => {
  const { data } = await apolloClient.query({
    query: graphql.getEmployeeSurvey,
    variables: {
      surveyCode,
      previewEmployeeId,
    },
    fetchPolicy: NO_CACHE,
  });

  return data.employeeSurvey;
};

const fetchEmployeeSurveyResult = async (surveyCode, companyId) => {
  const { data } = await apolloClient.query({
    query: graphql.getEmployeeSurveyResult,
    variables: {
      surveyCode,
      companyId,
    },
    fetchPolicy: NO_CACHE,
  });

  return data.employeeSurveyResult;
};

const fetchOpenSurvey = async (openSurveyExternalId, openSurveyAccessCode) => {
  const { data } = await apolloClient.query({
    query: graphql.openSurvey,
    variables: {
      openSurveyExternalId,
      openSurveyAccessCode: openSurveyAccessCode || null,
    },
    fetchPolicy: NO_CACHE,
  });

  return data.openSurvey;
};

const submitOpenSurveyResponse = async (
  openSurveyExternalId,
  openSurveyAccessCode,
  clientToken,
  idempotencyKey,
  answers,
) => {
  try {
    const res = await apolloClient.mutate({
      mutation: graphql.submitOpenSurveyResponse,
      variables: {
        openSurveyExternalId,
        openSurveyAccessCode: openSurveyAccessCode || null,
        clientToken,
        idempotencyKey,
        answers,
      },
    });

    return res.data.submitOpenSurveyResponse;
  } catch (err) {
    // A non-2xx response (the throttle's 429) reaches Apollo as a network error with the GraphQL errors only in the body.
    const graphQLError = err.graphQLErrors?.[0] ?? err.networkError?.result?.errors?.[0];
    const error = new Error(graphQLError?.message || err.message);
    error.translationKey = graphQLError?.extensions?.translationKey;
    throw error;
  }
};

const fetchLanguages = async () => {
  const { data } = await apolloClient.query({
    query: graphql.languages,
  });

  return data.languages;
};

// NOTE: All `answers` in the application state contain standard attributes
// which will be mixed in with the variables sent to the GraphQL query.
//
// Each answer looks like this:
// ```
// answer: {
//   value: 'some value', // could be a different type like an array or an integer
//   comment: 'this is my comment', // optional field not used by all answers
//   isSkipped: false // a boolean attribute indicating the user skipped the question
// }
// ```
//
// Combine the above with both `surveyCode` and `questionId` and that is all
// the data that is required for the GraphQL query.
const submitAnswer = async (surveyCode, questionId, questionType, answer) => {
  let mutation;

  switch (questionType) {
    case QUESTION_TYPES.RATING:
    case QUESTION_TYPES.ENPS:
    case QUESTION_TYPES.ICONS:
    case QUESTION_TYPES.YES_NO:
      mutation = graphql.submitScaleQuestion;
      break;
    case QUESTION_TYPES.MULTIPLE_CHOICE:
    case QUESTION_TYPES.DROPDOWN:
      mutation = graphql.submitMultipleQuestion;
      break;
    case QUESTION_TYPES.TEXT:
    default:
      mutation = graphql.submitTextQuestion;
  }

  const variables = {
    surveyCode,
    questionId,
    value: answer.value,
    isSkipped: answer.isSkipped,
    answerTimestamp: String(new Date().getTime()),
  };

  // Only include `comment` attribute if one exists.
  if (answer.comment !== undefined) variables.comment = answer.comment;

  try {
    const res = await apolloClient.mutate({
      mutation,
      variables,
    });

    const operationName = Object.keys(res.data)[0];

    return res.data[operationName];
  } catch (err) {
    return err.errors ? err.errors[0].message : String(err).replace('Error: GraphQL error: ', '');
  }
};

const submitSurvey = async (surveyCode) => {
  try {
    const res = await apolloClient.mutate({
      mutation: graphql.submitSurvey,
      variables: {
        surveyCode,
      },
    });

    return res.data.submitSurvey;
  } catch (err) {
    const isGraphqlError = err.errors && Array.isArray(err.errors);

    if (isGraphqlError) {
      throw new Error(err.errors[0].message);
    } else {
      throw err;
    }
  }
};

export default {
  fetchKioskSurveys,
  fetchKioskCompany,
  fetchEmployeeSurvey,
  fetchEmployeeSurveyResult,
  fetchOpenSurvey,
  submitOpenSurveyResponse,
  fetchLanguages,
  submitAnswer,
  submitSurvey,
};
